import React, { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { Keypad, PhoneKeypadIcon, selectCall, webrtcActions } from 'webrtc-client-janus';
import * as Phone from '../api/call-control';
import { useIsSoftphoneEnabled } from '../app-hooks';
import { useSelector } from '../hook-utils';
import { Access, CallState, ExtensionDevice, ExtensionStatus, Media, PbxAction, SERVER_PBX_MOBILE } from '../model';
import { actions } from '../store';
import { selectActiveDevice } from '../store/reducers';
import { selectMyAccess, selectMyData, selectMyDevices, selectMyself } from '../store/reducers/config';
import { getParkedMedia, selectedTableRowDialNumber } from '../store/reducers/localSettings';
import { selectHeldCall, selectMyActiveCall, selectMyCalls, selectQueueHeldCall } from '../store/reducers/media';
import { selectExtension } from '../store/reducers/queueAgents';
import { isTruthy } from '../type-utils';
import { activeCallCount } from './CallPopup';
import { ReactComponent as AddIcon } from './icons/add.svg';
import { ReactComponent as AnswerIcon } from './icons/callcontrol/answer.svg';
import { ReactComponent as BlindTransferIcon } from './icons/callcontrol/blind-transfer.svg';
import { ReactComponent as CancelTransferIcon } from './icons/callcontrol/cancel-transfer.svg';
import { ReactComponent as CompleteTransferIcon } from './icons/callcontrol/complete-transfer.svg';
import { ReactComponent as DialIcon } from './icons/callcontrol/dial.svg';
import { ReactComponent as HangupIcon } from './icons/callcontrol/hangup.svg';
import { ReactComponent as HoldForIcon } from './icons/callcontrol/hold-for.svg';
import { ReactComponent as HoldIcon } from './icons/callcontrol/hold.svg';
import { ReactComponent as HoldMessageIcon } from './icons/callcontrol/hold-message.svg';
import { ReactComponent as IntrudeIcon } from './icons/callcontrol/intrude.svg';
import { ReactComponent as MonitorIcon } from './icons/callcontrol/monitor.svg';
import { ReactComponent as PickUpIcon } from './icons/callcontrol/pickup.svg';
import { ReactComponent as QueueHoldIcon } from './icons/callcontrol/queue-hold.svg';
import { ReactComponent as QueueResumeIcon } from './icons/callcontrol/queue-resume.svg';
import { ReactComponent as ResumeIcon } from './icons/callcontrol/resume.svg';
import { ReactComponent as SupervisedTransferIcon } from './icons/callcontrol/supervised-transfer.svg';
import './MainToolbar.scss';
import './MainToolbarDialButton.scss';
import { MenuDivider, MenuItem, SubMenu } from './Menu';
import SplitButton from './SplitButton';

export default function MainToolbarDialButton() {
  const dispatch = useDispatch()

  const myId = useSelector(selectMyself)
  const extension = useSelector(s => myId && selectExtension(s, myId))
  const me = useSelector(selectMyData)
  const access = useSelector(selectMyAccess)

  const calls = useSelector(selectMyCalls)
  const activeCall = useSelector(selectMyActiveCall)
  const heldCall = useSelector(selectHeldCall)
  const devices = useSelector(selectMyDevices)
  const activeDevice = useSelector(selectActiveDevice)
  const queueHoldMedia = useSelector(selectQueueHeldCall)
  const parked = useSelector(getParkedMedia)
  const isSoftphoneEnabled = useIsSoftphoneEnabled()
  const webrtcCall = useSelector(selectCall)
  const number = useSelector(selectedTableRowDialNumber)

  const isWebRtcCallActive = useCallback(() => {
    const result = isSoftphoneEnabled && webrtcCall && (webrtcCall.status === 'connected' || webrtcCall.status === 'reconnected')
    return result
  }, [isSoftphoneEnabled, webrtcCall])
  const showKeypad = isWebRtcCallActive()

  function onDigit(digit: string) {
    if (webrtcCall.canSendDtmf) {
      dispatch(webrtcActions.dtmf(digit))
    }
  }

  if (!myId || !me || !extension || !activeDevice || !devices) {
    return <SplitButton items={[]}>
      <DialIcon />
      Dial
    </SplitButton>
  }

  const callCount = activeCallCount(calls, devices.devices)

  const control = GetCallControl(activeCall, heldCall, activeDevice, callCount, queueHoldMedia, parked, false,
    {
      access: access,
      mobile: me.mobile,
      associate: me.associate,
      status: extension.status,
      canReceiveVoicemail: me.canReceiveVoicemail,
      devices: devices.devices,
    })

  const inSupervisedTransfer = control.cancelTransfer.enabled
  const items = [
    (control.dial.visible) && <MenuItem key='Dial' disabled={!control.dial.enabled} onClick={() =>

      dispatch(actions.openSmartSearch('dial',
        extension.status,
        extension.subStatus,
        activeDevice.number,
        inSupervisedTransfer,
        activeCall,
        number))

    }><DialIcon /> Dial</MenuItem>,
    showKeypad && <MenuItem key='Keypad' subMenu={<SubMenu><MenuItem itemClass='menu-keypad' noIcon={true}>
      <Keypad onKeyPressed={onDigit} className='keypadFlyout' />
    </MenuItem></SubMenu>}><PhoneKeypadIcon /> Keypad</MenuItem>,
    (control.answer.visible) && <MenuItem key='Answer' disabled={!control.answer.enabled} onClick={() => dispatch(actions.ccAnswerMedia(activeDevice, activeCall))}><AnswerIcon /> Answer</MenuItem>,
    (control.hangup.visible) && <MenuItem key='Hangup' disabled={!control.hangup.enabled} onClick={() => dispatch(actions.ccHangUp(activeDevice.number, activeCall))}><HangupIcon /> Hangup</MenuItem>,
    <MenuDivider key='d1' />,
    (control.blindTransfer.visible) && <MenuItem key='BlindTransferransfer' disabled={!control.blindTransfer.enabled} onClick={() =>

      dispatch(actions.openSmartSearch('blind-transfer',
        extension.status,
        extension.subStatus,
        activeDevice.number,
        inSupervisedTransfer,
        activeCall,
        number))

    }><BlindTransferIcon /> Blind Transfer</MenuItem>,
    (control.supervisedTransfer.visible) && <MenuItem key='SupervisedTransferransfer' disabled={!control.supervisedTransfer.enabled} onClick={() =>

      dispatch(actions.openSmartSearch('supervised-transfer',
        extension.status,
        extension.subStatus,
        activeDevice.number,
        inSupervisedTransfer,
        activeCall,
        number))

    }><SupervisedTransferIcon /> Supervised Transfer</MenuItem>,
    (control.completeTransfer.visible) && <MenuItem key='CompleteTransferransfer' disabled={!control.completeTransfer.enabled} onClick={() => Phone.completeTransfer(activeDevice.number)}><CompleteTransferIcon /> Complete Transfer</MenuItem>,
    (control.cancelTransfer.visible) && <MenuItem key='CancelTransferransfer' disabled={!control.cancelTransfer.enabled} onClick={() => Phone.cancelTransfer(activeDevice.number)}><CancelTransferIcon /> Cancel Transfer</MenuItem>,
    <MenuDivider key='d2' />,
    (control.hold.visible) && <MenuItem key='Hold' disabled={!control.hold.enabled} onClick={() => Phone.hold(activeDevice.number)}><HoldIcon /> Hold</MenuItem>,
    (control.hold.visible && access.features.phoneHoldMessage) && <MenuItem key='HoldMessage' disabled={!control.hold.enabled} onClick={() => Phone.holdMessage(activeDevice.number, -1)}><HoldMessageIcon /> Hold for message</MenuItem>,
    (control.resume.visible) && <MenuItem key='Resume' disabled={!control.resume.enabled} onClick={() => Phone.resume(activeDevice.number)}><ResumeIcon /> Resume</MenuItem>,
    (control.queueHold.visible) && <MenuItem key='QueueHoldold' disabled={!control.queueHold.enabled} onClick={() => {
      dispatch(actions.ccQueueHold(activeDevice.number, '', activeCall ? activeCall.id : '', activeCall ? activeCall.mediaType : ''))
    }
    }><QueueHoldIcon />Park</MenuItem>,
    (control.holdFor.visible) && <MenuItem key='HoldForor' disabled={!control.holdFor.enabled} onClick={() =>

      dispatch(actions.openSmartSearch('hold-for',
        extension.status,
        extension.subStatus,
        activeDevice.number,
        inSupervisedTransfer,
        activeCall,
        number))

    }><HoldForIcon />Park For...</MenuItem>,
    (control.queueResume.visible) && <MenuItem key='QueueResumeesume' disabled={!control.queueResume.enabled} onClick={() => {
      //clear queue hold
      dispatch(actions.setQueueHoldKey(""))
      if (queueHoldMedia)
        dispatch(actions.ccQueueResume(myId.id, [queueHoldMedia]))
    }
    }><QueueResumeIcon />Unpark</MenuItem>,
    <MenuDivider key='d3' />,
    (control.pickup.visible) && <MenuItem key='PickUp' disabled={!control.pickup} onClick={() =>

      dispatch(actions.openSmartSearch('pickup',
        extension.status,
        extension.subStatus,
        activeDevice.number,
        inSupervisedTransfer,
        activeCall,
        number))

    }><PickUpIcon /> Pickup</MenuItem>,
    (control.confernce.visible) && <MenuItem key='Conference' disabled={!control.confernce.enabled} onClick={() => {

      dispatch(actions.openSmartSearch('conference',
        extension.status,
        extension.subStatus,
        activeDevice.number,
        inSupervisedTransfer,
        activeCall,
        number))

    }}><AddIcon /> Conference</MenuItem>,
    (control.monitor.visible) && <MenuItem key='Monitor' disabled={!control.monitor.enabled} onClick={() =>

      dispatch(actions.openSmartSearch('monitor',
        extension.status,
        extension.subStatus,
        activeDevice.number,
        inSupervisedTransfer,
        activeCall,
        number))

    }><MonitorIcon /> Monitor</MenuItem>,
    (control.intrude.visible) && <MenuItem key='Intrude' disabled={!control.intrude.enabled} onClick={() =>

      dispatch(actions.openSmartSearch('intrude',
        extension.status,
        extension.subStatus,
        activeDevice.number,
        inSupervisedTransfer,
        activeCall,
        number))

    }><IntrudeIcon /> Intrude</MenuItem>,
  ].filter(isTruthy)

  let text = 'Dial'
  let icon = <DialIcon />
  let title = 'Dial'
  switch (extension.status) {
    case ExtensionStatus.Ringing:
      {
        icon = <AnswerIcon />
        text = 'Answer'
        break
      }
    case ExtensionStatus.OffHook:
    case ExtensionStatus.Dialed:
    case ExtensionStatus.Answered:
      {
        icon = <HangupIcon />
        text = 'Hang Up'
        break
      }
    case ExtensionStatus.Hold:
      {
        icon = <ResumeIcon />
        text = 'Resume'
        break
      }
    case ExtensionStatus.Released:
    case ExtensionStatus.None:
    default:
      {
        icon = <DialIcon />
        text = 'Dial'
        break
      }
  }

  title = text
  if (activeDevice !== null) {
    if (activeDevice.baseType === 0) {
      switch (activeDevice.number) {
        case me.mobile:
          title = title + ' on Mobile Phone'
          break
        case me.home:
          title = title + ' on Home Phone'
          break
        case me.other:
          title = title + ' on Other Phone'
          break
        default:
          title = title + ' on Desk Phone'
          break
      }
    } else {
      title = title + ' on Softphone'
    }
  } else {
    console.error('no primary or current device')
  }

  return <SplitButton className='MainToolbarDialButton' title={title} onClick={() => {
    switch (extension.status) {
      case ExtensionStatus.Ringing:
        {
          dispatch(actions.ccAnswerMedia(activeDevice, activeCall))
          break
        }
      case ExtensionStatus.OffHook:
      case ExtensionStatus.Dialed:
      case ExtensionStatus.Answered:
        {
          dispatch(actions.ccHangUp(activeDevice.number, activeCall))
          break
        }
      case ExtensionStatus.Hold:
        {
          Phone.resume(activeDevice.number, activeCall ? activeCall.id : '')
          break
        }
      case ExtensionStatus.Released:
      case ExtensionStatus.None:
      default:
        {
          if (control && control.dial.enabled) {
            // open smart search window
            dispatch(actions.openSmartSearch('dial',
              extension.status,
              extension.subStatus,
              activeDevice.number,
              inSupervisedTransfer,
              activeCall,
              number))
          } else {
            console.info('MainToolbarDialButton dial disabled')
          }
          break
        }
    }

  }} items={items}>
    {icon}
    {text}
  </SplitButton>
}

export function GetCallControl(activeCall: Media | null, heldCall: Media | null, device: ExtensionDevice, callsCount: number, queueHoldMedia: Media | null, parked: string[], assistant: boolean, me: {
  access: Access,
  mobile: string,
  associate: string,
  status: ExtensionStatus,
  canReceiveVoicemail: boolean,
  devices: ExtensionDevice[],
}) {

  // device here needs to be either the primary device as not a call
  // or the device that is matched to the extensions current call (2057 message)

  // These dont need a call as they can only be performed with the Device is Idle.
  let dial = me.status === ExtensionStatus.Released || me.status === ExtensionStatus.None
  let pickup = me.status === ExtensionStatus.Released || me.status === ExtensionStatus.None
  let intrude = me.status === ExtensionStatus.Released || me.status === ExtensionStatus.None
  let monitor = me.status === ExtensionStatus.Released || me.status === ExtensionStatus.None

  let answer: boolean = false, hangup: boolean = false, hold: boolean = false, resume: boolean = false, blindTransfer: boolean = false,
    superTransfer: boolean = false, cancelTransfer: boolean = false, completetTransfer: boolean = false, conference: boolean = false,
    holdFor: boolean = false, queueHold: boolean = false, queueResume: boolean = false, forwardToVoicemail: boolean = false, forwardToMobile: boolean = false,
    forwardToAssociate: boolean = false, record: boolean = false, stopRecording: boolean = false, suppressRecording: boolean = false, allowRecording: boolean = false,
    listenToVoicemail: boolean = false, holdAndRetrive: boolean = false, retrieveFromVoiceMail: boolean = false, sendToMobile: boolean = false

  if (!activeCall) {
    answer = (me.status === ExtensionStatus.Ringing)
    hangup = (me.status === ExtensionStatus.OffHook || me.status === ExtensionStatus.Dialed || me.status === ExtensionStatus.Answered)
    hold = me.status === ExtensionStatus.Answered
    resume = me.status === ExtensionStatus.Hold
    blindTransfer = me.status === ExtensionStatus.Answered
    superTransfer = me.status === ExtensionStatus.Answered
    cancelTransfer = false //VB code has a check but not sure it there is a need if there is no active call CanCancelTransfer(null, media)
    completetTransfer = false //VB code has a check but not sure it there is a need if there is no active call CanCancelTransfer(null, media
    conference = me.status === ExtensionStatus.Answered
    holdFor = me.status === ExtensionStatus.Answered
    queueHold = me.status === ExtensionStatus.Answered
    queueResume = (me.status === ExtensionStatus.Released || me.status === ExtensionStatus.None) && queueHoldMedia !== null // Need queue hold logic on me and server m_oUser.me.HasCallOnQueueHold

    //recording
    //stopRecording

    forwardToVoicemail = me.status === ExtensionStatus.Ringing
    forwardToAssociate = me.status === ExtensionStatus.Ringing
    forwardToMobile = me.status === ExtensionStatus.Ringing
    sendToMobile = me.status === ExtensionStatus.Answered


  } else {

    //if (activeCall.status !== CallState.QueueHold && activeCall.extensionsWaitingForWrapup && activeCall.extensionsWaitingForWrapup.length > 0 && me.devices.findIndex(d => d.number === activeCall.deviceId) === -1 && activeCall.extensionsWaitingForWrapup.findIndex(e => me.devices.findIndex(d => d.number === e) !== -1) !== -1) {
    //users active call is in waiting to wrapup

    answer = activeCall.status === CallState.ExtensionPending && (me.status === ExtensionStatus.None || me.status === ExtensionStatus.Released || me.status === ExtensionStatus.Ringing)
    hangup = (activeCall.status === CallState.OutboundOffHook || activeCall.status === CallState.OutboundRinging ||
      activeCall.status === CallState.ExtensionAnswered || activeCall.status === CallState.ExtensionTransfer || activeCall.status === CallState.Hold)
    hold = activeCall.status === CallState.ExtensionAnswered && callsCount === 1
    resume = activeCall.status === CallState.Hold && callsCount === 1
    blindTransfer = activeCall.status === CallState.ExtensionAnswered && callsCount === 1 // Only on 1 call and it's Answered
    superTransfer = activeCall.status === CallState.ExtensionAnswered && callsCount === 1 // Only on 1 call and it's Answered
    cancelTransfer = activeCall.status !== CallState.WaitingForWrapup && canCancelTransfer(activeCall, heldCall) && !assistant
    completetTransfer = activeCall.status !== CallState.WaitingForWrapup && canCompleteTransfer(activeCall, heldCall, me.access.isLyncLicensed) && !assistant
    conference = me.status === ExtensionStatus.Answered && activeCall.status === CallState.ExtensionAnswered && activeCall.sourceApp !== SERVER_PBX_MOBILE
    holdFor = canQueueHold(activeCall)
    queueHold = canQueueHold(activeCall)
    queueResume = (activeCall.status === CallState.QueueHold || activeCall.status === CallState.Queueing || (activeCall.status === CallState.WaitingForWrapup && parked.findIndex(id => id === activeCall.id) !== -1)) &&
      (me.status === ExtensionStatus.Released || me.status === ExtensionStatus.None) &&
      activeCall.sourceApp !== SERVER_PBX_MOBILE

    record = activeCall.status === CallState.ExtensionAnswered && activeCall.sourceApp !== SERVER_PBX_MOBILE && !activeCall.isRecording
      && activeCall.voicemailPortIsSuppresed === "allowed" //todo And Not bIsCollaborationServerCall
    stopRecording = activeCall.status === CallState.ExtensionAnswered && activeCall.sourceApp !== SERVER_PBX_MOBILE && activeCall.isRecording
      && activeCall.voicemailPortIsSuppresed === "allowed"
    suppressRecording = activeCall.status === CallState.ExtensionAnswered && activeCall.sourceApp !== SERVER_PBX_MOBILE
      && activeCall.voicemailPortIsSuppresed === "allowed" //todo And Not bIsCollaborationServerCall and oDevice.IsRecordingToOtherMailBox
    allowRecording = activeCall.status === CallState.ExtensionAnswered && activeCall.sourceApp !== SERVER_PBX_MOBILE &&
      (activeCall.voicemailPortIsSuppresed === "recordingSuppressed" || activeCall.voicemailPortIsSuppresed === "suppressed")

    forwardToVoicemail = activeCall.status === CallState.ExtensionPending && !activeCall.isQueueCall
    forwardToAssociate = activeCall.status === CallState.ExtensionPending && !activeCall.isQueueCall && activeCall.sourceApp !== SERVER_PBX_MOBILE
    forwardToMobile = activeCall.status === CallState.ExtensionPending && !activeCall.isQueueCall && activeCall.deviceId !== me.mobile
    sendToMobile = activeCall.status === CallState.ExtensionAnswered && !activeCall.isQueueCall && activeCall.deviceId !== me.mobile


    listenToVoicemail = (me.status === ExtensionStatus.Released || me.status === ExtensionStatus.None) && activeCall.status === CallState.CallInVoicemail && (me.status === ExtensionStatus.Released || me.status === ExtensionStatus.None) && activeCall.inVmRecordingMessage
    retrieveFromVoiceMail = (me.status === ExtensionStatus.Released || me.status === ExtensionStatus.None) && activeCall.status === CallState.CallInVoicemail
    holdAndRetrive = me.status === ExtensionStatus.Answered && (activeCall.status === CallState.CallInVoicemail || activeCall.status === CallState.QueueHold || activeCall.status === CallState.Queueing) && activeCall.sourceApp !== SERVER_PBX_MOBILE && activeCall.clid !== activeCall.queueId
    //(oCall.Status = psCallStateInVoicemail Or oCall.IsInPersonalQueue(m_oExtension.Number) Or oCall.Status = psCallStateQueueHold) And (Not oCall.IsMobileDirectCall) And (Not IsExtensionCallingOwnVoicemailInbox(m_oExtension, m_oCalls))

  }
  forwardToVoicemail = forwardToVoicemail && me.canReceiveVoicemail
  forwardToAssociate = forwardToAssociate && me.associate !== ""
  forwardToMobile = forwardToMobile && me.mobile !== ""
  sendToMobile = sendToMobile && me.mobile !== ""

  //console.info('me.access.features.phoneHoldFor ' + me.access.features.phoneHoldFor + ' (PbxAction.HoldFor & device.pbxActionBitMask) !== 0) ' + ((PbxAction.HoldFor & device.pbxActionBitMask) !== 0 ? 'true' : 'false'))

  return {
    dial: {
      visible: (me.access.features.phoneDial && (PbxAction.Dial & device.pbxActionBitMask) !== 0),
      enabled: dial,
    },
    answer: {
      visible: (me.access.features.phoneAnswer && (PbxAction.Answer & device.pbxActionBitMask) !== 0),
      enabled: answer,
    },
    hangup: {
      visible: (me.access.features.phoneHangup && (PbxAction.Hangup & device.pbxActionBitMask) !== 0),
      enabled: hangup,
    },
    hold: {
      visible: (me.access.features.phoneHold && (PbxAction.Hold & device.pbxActionBitMask) !== 0),
      enabled: hold,
    },
    resume: {
      visible: (me.access.features.phoneResume && (PbxAction.CancelHold & device.pbxActionBitMask) !== 0),
      enabled: resume,
    },

    blindTransfer: {
      visible: (me.access.features.phoneXferBlind && (PbxAction.BlindTransfer & device.pbxActionBitMask) !== 0),
      enabled: blindTransfer,
    },
    supervisedTransfer: {
      visible: (me.access.features.phoneXferSupervised && (PbxAction.SupervisedTransfer & device.pbxActionBitMask) !== 0),
      enabled: superTransfer,
    },
    completeTransfer: {
      visible: (me.access.features.phoneXferComplete && (PbxAction.CompleteTransfer & device.pbxActionBitMask) !== 0),
      enabled: completetTransfer,
    },
    cancelTransfer: {
      visible: (me.access.features.phoneXferCancel && (PbxAction.CancelTransfer & device.pbxActionBitMask) !== 0),
      enabled: cancelTransfer,
    },
    switchHold: {
      visible: ((PbxAction.SwitchHold & device.pbxActionBitMask) !== 0),
      enabled: false,
    },


    queueHold: {
      visible: (me.access.features.phoneQueueHold && (PbxAction.QueueHold & device.pbxActionBitMask) !== 0),
      enabled: queueHold,
    },
    queueResume: {
      visible: (me.access.features.phoneQueueHold && (PbxAction.QueueHold & device.pbxActionBitMask) !== 0),
      enabled: queueResume,
    },


    record: {
      visible: (PbxAction.Record & device.pbxActionBitMask) !== 0 && ((me.access.features.phoneSuppressRecord || me.access.features.phoneRecord)),
      enabled: record,
    },
    stopRecording: {
      visible: (PbxAction.Record & device.pbxActionBitMask) !== 0 && ((me.access.features.phoneSuppressRecord || me.access.features.phoneRecord)),
      enabled: stopRecording,
    },
    suppressRecording: {
      visible: (PbxAction.Record & device.pbxActionBitMask) !== 0 && ((me.access.features.phoneSuppressRecord || me.access.features.phoneRecord) && me.access.isCallRecordingLicensed),
      enabled: suppressRecording,
    },
    allowRecording: {
      visible: (PbxAction.Record & device.pbxActionBitMask) !== 0 && ((me.access.features.phoneSuppressRecord || me.access.features.phoneRecord) && me.access.isCallRecordingLicensed),
      enabled: allowRecording,
    },


    pickup: {
      visible: (me.access.features.phonePickup && (PbxAction.Pickup & device.pbxActionBitMask) !== 0),
      enabled: pickup,
    },
    holdFor: {
      visible: (me.access.features.phoneHoldFor && (PbxAction.HoldFor & device.pbxActionBitMask) !== 0),
      enabled: holdFor,
    },
    confernce: {
      visible: (me.access.features.phoneConference && conference && (PbxAction.Conference & device.pbxActionBitMask) !== 0),
      enabled: true,
    },
    intrude: {
      visible: (me.access.features.phoneIntrude && (PbxAction.Intrude & device.pbxActionBitMask) !== 0),
      enabled: intrude,
    },
    monitor: {
      visible: (me.access.features.phoneMonitor && (PbxAction.Moniotor & device.pbxActionBitMask) !== 0) && me.access.canMonitor,
      enabled: monitor,
    },

    sendToMobile: {
      visible: !me.access.isLyncLicensed,
      enabled: sendToMobile,
    },
    forwardToAssociate: {
      visible: !me.access.isLyncLicensed,
      enabled: forwardToAssociate && !assistant,
    },
    forwardToMobile: {
      visible: !me.access.isLyncLicensed,
      enabled: forwardToMobile,
    },
    forwardToVoiceMail: {
      visible: me.access.features.phoneSendCallToVM,
      enabled: forwardToVoicemail,
    },
    retrieveFromVoiceMail: {
      visible: me.access.features.phoneRetrieveCallFromVM,
      enabled: retrieveFromVoiceMail,
    },
    listenToVoicemail: {
      visible: (PbxAction.Moniotor & device.pbxActionBitMask) !== 0 && me.access.features.phoneListenToCallInVM,
      enabled: listenToVoicemail,
    },
    holdAndRetrive: {
      visible: (me.access.features.phoneRetrieveCallFromVM && me.access.features.licensedEmpower),
      enabled: holdAndRetrive,
    },

  }
}

export function canCancelTransfer(activeCall: Media, heldCall: Media | null) {
  if (!heldCall) { return false }

  // should be two differnet ones.
  return activeCall.id !== heldCall.id
}

function canCompleteTransfer(activeCall: Media, heldCall: Media | null, isLync?: boolean) {
  if (!heldCall) { return false }

  if (isLync) {
    if (!(activeCall.status === CallState.ExtensionAnswered || activeCall.status === CallState.ExtensionTransfer)) {
      return false
    }
  }

  // should be two differnet ones.
  return activeCall.id !== heldCall.id
}

function canQueueHold(call: Media) {
  return call.mediaType !== 'VM' && call.status === CallState.ExtensionAnswered
}
